import { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import axios from 'axios';
import VotingABI from '../abi/Voting.json';
import CandidateImage from './CandidateImage';

const CONTRACT_ADDRESS = process.env.REACT_APP_CONTRACT_ADDRESS;
const SEPOLIA_RPC_URL  = process.env.REACT_APP_SEPOLIA_RPC_URL;
const BACKEND_URL      = process.env.REACT_APP_BACKEND_URL;

export default function P06LiveResult({ votedCandidateId, onEndedClick }) {
  const [candidates, setCandidates] = useState([]);
  const [totalVotes, setTotalVotes] = useState(0);
  const pollRef = useRef(null);

  useEffect(() => { loadAndPoll(); return () => stopPoll(); }, []);

  async function loadAndPoll() {
    await loadCandidates();
    startPoll();
  }

  async function loadCandidates() {
    try {
      const provider = new ethers.JsonRpcProvider(SEPOLIA_RPC_URL);
      const contract = new ethers.Contract(CONTRACT_ADDRESS, VotingABI.abi, provider);
      const [onchain, total] = await Promise.all([
        contract.getCandidates(),
        contract.getTotalVotes(),
      ]);
      let offchain = [];
      try { offchain = await axios.get(BACKEND_URL + '/candidates').then(r => r.data); } catch {}
      const merged = onchain.map(c => {
        const off = offchain.find(o => o.id === Number(c.id)) || {};
        return { id: Number(c.id), name: c.name, voteCount: Number(c.voteCount),
          imageUrl: off.imageUrl || null, description: off.description || '' };
      });
      setCandidates(merged);
      setTotalVotes(Number(total));
    } catch (e) { console.error('데이터 로딩 오류:', e); }
  }

  function startPoll() {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const provider = new ethers.JsonRpcProvider(SEPOLIA_RPC_URL);
        const contract = new ethers.Contract(CONTRACT_ADDRESS, VotingABI.abi, provider);
        const state = await contract.getState();
        if (state === 2n) { stopPoll(); onEndedClick(); return; }
        const [list, total] = await Promise.all([
          contract.getCandidates(), contract.getTotalVotes(),
        ]);
        setCandidates(prev => prev.map(c => {
          const u = list.find(x => Number(x.id) === c.id);
          return u ? { ...c, voteCount: Number(u.voteCount) } : c;
        }));
        setTotalVotes(Number(total));
      } catch (e) { console.error('폴링 오류:', e); }
    }, 5000);
  }

  function stopPoll() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }

  const maxVotes = candidates.length > 0 ? Math.max(...candidates.map(c => c.voteCount)) : 0;
  const votedName = votedCandidateId != null
    ? (candidates.find(c => c.id === votedCandidateId) || {}).name || ''
    : null;

  return (
    <div className="ev-page">
      <div className="ev-page-header">
        <div className="ev-live-banner">
          <div className="ev-live-dot" />
          <span className="ev-live-label">LIVE</span>
          <h1 className="ev-page-title" style={{ marginBottom: 0 }}>실시간 투표 현황</h1>
        </div>
        <div className="ev-total-count" style={{ marginTop: 12 }}>
          총 투표 수 <strong>{totalVotes}표</strong>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-sub)' }}>&nbsp;· 5초마다 갱신</span>
        </div>
      </div>

      {votedName && (
        <div className="ev-my-vote-notice">
          내 투표: <strong>{votedName}</strong> 후보
        </div>
      )}

      <div className="ev-result-list">
        {candidates
          .slice()
          .sort((a, b) => b.voteCount - a.voteCount)
          .map((c, idx) => {
            const pct = totalVotes > 0 ? ((c.voteCount / totalVotes) * 100).toFixed(1) : '0.0';
            const barW = maxVotes > 0 ? ((c.voteCount / maxVotes) * 100).toFixed(0) : 0;
            const isLead = maxVotes > 0 && c.voteCount === maxVotes;
            const isVoted = c.id === votedCandidateId;
            return (
              <div key={c.id}
                className={`ev-result-item ${isLead ? 'ev-result-item--leader' : ''} ${isVoted ? 'ev-result-item--my' : ''}`}>
                <div className="ev-result-item__rank">{idx + 1}</div>
                <CandidateImage
                  src={c.imageUrl ? BACKEND_URL + c.imageUrl : null}
                  alt={c.name}
                  circle
                  width={60}
                  height={60}
                />
                <div className="ev-result-item__info">
                  <div className="ev-result-item__name">
                    {c.name}
                    {isLead && <span className="ev-leader-badge">▲ 선두</span>}
                    {isVoted && <span className="ev-my-badge">내 선택</span>}
                  </div>
                  {c.description && (
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-sub)', marginBottom: 4 }}>
                      {c.description}
                    </div>
                  )}
                  <div className="ev-progress-wrap" style={{ marginTop: 8 }}>
                    <div className={`ev-progress-bar ${isLead ? 'ev-progress-bar--leader' : ''}`}
                      style={{ width: `${barW}%` }} />
                  </div>
                </div>
                <div>
                  <div className="ev-result-item__pct">{pct}%</div>
                  <div className="ev-result-item__count">{c.voteCount}표</div>
                </div>
              </div>
            );
          })}
      </div>
      <p style={{ marginTop: 20, fontSize: '0.8rem', color: 'var(--text-sub)', textAlign: 'center' }}>
        * 투표가 종료되면 자동으로 최종 결과 화면으로 이동합니다.
      </p>
    </div>
  );
}
